import { Card } from "@/components/ui/card";
import { Droplets, AlertCircle } from "lucide-react";

interface BladderDiaryCalendarViewProps {
  intakeEntries: Array<{ time: string; amount: number; type: string }>;
  urinationEntries: Array<{ 
    time: string;
    urgency: number; 
    hadLeakage: boolean; 
    leakageAmount?: string; 
    notes?: string 
  }>;
}

export const BladderDiaryCalendarView = ({ 
  intakeEntries, 
  urinationEntries 
}: BladderDiaryCalendarViewProps) => {
  const getUrgencyColor = (level: number) => {
    if (level <= 2) return "text-green-600 dark:text-green-400";
    if (level <= 3) return "text-yellow-600 dark:text-yellow-400";
    return "text-orange-600 dark:text-orange-400";
  };

  const getUrgencyLabel = (level: number) => {
    if (level <= 2) return "Low";
    if (level <= 3) return "Moderate";
    return "High";
  };

  if (intakeEntries.length === 0 && urinationEntries.length === 0) {
    return (
      <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border border-border/50 rounded-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <Droplets className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Bladder Diary</h3>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <Droplets className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No bladder diary entries for this date</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border border-border/50 rounded-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-primary/10">
          <Droplets className="h-5 w-5 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Bladder Diary</h3>
      </div>

      <div className="space-y-4">
        {/* Intake Section */}
        {intakeEntries.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-primary mb-3">Fluid Intake</h4>
            <div className="space-y-2">
              {intakeEntries.map((entry, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-muted-foreground bg-background px-2 py-1 rounded">
                      {entry.time}
                    </span>
                    <span className="text-sm text-foreground capitalize">{entry.type}</span>
                  </div>
                  <span className="text-sm font-bold text-primary">{entry.amount}ml</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Urination Section */}
        {urinationEntries.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-accent mb-3">Bathroom Visits</h4>
            <div className="space-y-2">
              {urinationEntries.map((entry, index) => (
                <div 
                  key={index}
                  className="p-3 bg-accent/5 rounded-lg border border-accent/20"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground bg-background px-2 py-1 rounded">
                      {entry.time}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Urgency:</span>
                      <span className={`text-sm font-bold ${getUrgencyColor(entry.urgency)}`}>
                        {getUrgencyLabel(entry.urgency)} ({entry.urgency}/5)
                      </span>
                    </div>
                  </div>
                  
                  {entry.hadLeakage && (
                    <div className="flex items-start gap-2 mt-2 p-2 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 text-xs">
                        <span className="font-semibold text-red-600">Leakage: </span>
                        <span className="text-red-600">{entry.leakageAmount || 'Not specified'}</span>
                      </div>
                    </div>
                  )}
                  
                  {entry.notes && (
                    <div className="mt-2 text-xs text-muted-foreground italic p-2 bg-background/50 rounded">
                      "{entry.notes}"
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="pt-4 border-t border-border/50 grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Total Intake</p>
            <p className="text-lg font-bold text-primary">
              {intakeEntries.reduce((sum, entry) => sum + entry.amount, 0)} ml
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Bathroom Trips</p>
            <p className="text-lg font-bold text-accent">
              {urinationEntries.length}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};
